import { useI18n, type CommonKey, type Lang } from '@hive/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';
import { Spinner } from './layout';
import { Markdown } from './Markdown';
import { useConfirm, useToast } from './overlay';
import { Switch } from './fields';

/**
 * Ajuda sensível ao contexto (HU-017/018/019). A fonte do conteúdo é uma interface:
 * a plataforma usa a API de ajuda do núcleo; a console do provedor usa conteúdo local.
 */
export interface HelpArticleData {
  id?: string;
  title?: string;
  sectionName?: string;
  markdown?: string;
  headings?: Array<{ title?: string; anchor?: string }>;
}
export interface HelpHitData {
  id?: string;
  title?: string;
  section?: string;
  snippet?: string;
  anchor?: string;
}
export interface HelpAnswerData {
  answered?: boolean;
  /** Assistente com IA (HU-1111): resposta em texto citando as seções [S1]…; sem IA, `reason` diz por quê. */
  aiUsed?: boolean;
  answer?: string;
  reason?: string;
  sections?: HelpHitData[];
  suggestedWizard?: string;
  support?: { offerTicket?: boolean; question?: string; screen?: string } | Record<string, unknown>;
}
export interface HelpSource {
  context(q: { screen: string; wizard?: string; step?: string; lang: Lang }): Promise<{ article?: HelpArticleData; stepText?: string }>;
  /** `signal`: o painel cancela a busca anterior quando a pessoa continua digitando. */
  search(q: { q: string; screen: string; lang: Lang; signal?: AbortSignal }): Promise<HelpHitData[]>;
  article(q: { id: string; lang: Lang }): Promise<HelpArticleData>;
  ask(q: { question: string; screen: string; lang: Lang; wizard?: string; step?: string }): Promise<HelpAnswerData>;
}

export interface WizardHelpInfo {
  id: string;
  stepId: string;
  n: number;
  total: number;
  title: string;
}

interface HelpCtxValue {
  screen: string;
  setScreen: (s: string) => void;
  wizard: WizardHelpInfo | null;
  setWizard: (w: WizardHelpInfo | null) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  whatIs: boolean;
  setWhatIs: (v: boolean) => void;
  /** Abre o artigo inteiro na central de ajuda (manual); ausente = o painel não oferece o link. */
  openManual?: (id: string, anchor?: string) => void;
}

const HelpCtx = createContext<HelpCtxValue | null>(null);

/** Igual a useHelp, mas sem exigir o provedor (o assistente funciona fora da ajuda, ex.: em testes). */
export function useOptionalHelp(): HelpCtxValue | null {
  return useContext(HelpCtx);
}

export function useHelp(): HelpCtxValue {
  const v = useContext(HelpCtx);
  if (!v) throw new Error('useHelp fora do HelpProvider');
  return v;
}

/**
 * Cada tela declara o próprio código (ex.: 'assets', 'software-removal') para a ajuda abrir no artigo certo: o núcleo
 * escolhe o artigo cujo `screens:` contém o código. Fora do HelpProvider (telas públicas: login, Loja do dispositivo,
 * inscrição do celular) não faz nada.
 */
export function useHelpScreen(screen: string) {
  const help = useContext(HelpCtx);
  const setScreen = help?.setScreen;
  const setWizard = help?.setWizard;
  useEffect(() => {
    if (!setScreen || !setWizard) return;
    setScreen(screen);
    setWizard(null);
  }, [screen, setScreen, setWizard]);
}

export function HelpProvider({
  source,
  onOpenWizard,
  onOpenManual,
  children,
}: {
  source: HelpSource;
  /** "Abrir o assistente" sugerido pela resposta (id do assistente). */
  onOpenWizard?: (id: string) => void;
  /** "Abrir no manual": leva ao artigo inteiro na central de ajuda. */
  onOpenManual?: (id: string, anchor?: string) => void;
  children: ReactNode;
}) {
  const [screen, setScreen] = useState('home');
  const [wizard, setWizard] = useState<WizardHelpInfo | null>(null);
  const [open, setOpen] = useState(false);
  const [whatIs, setWhatIs] = useState(false);
  const value = useMemo(
    () => ({ screen, setScreen, wizard, setWizard, open, setOpen, whatIs, setWhatIs, openManual: onOpenManual }),
    [screen, wizard, open, whatIs, onOpenManual],
  );
  return (
    <HelpCtx.Provider value={value}>
      {children}
      {open && <HelpPanel source={source} onOpenWizard={onOpenWizard} />}
      <WhatIsThis enabled={whatIs} />
    </HelpCtx.Provider>
  );
}

/** Botão "?" da barra superior. */
export function HelpButton() {
  const { t } = useI18n<CommonKey>();
  const { open, setOpen } = useHelp();
  return (
    <button type="button" className="icon-btn" id="help-btn" aria-label={t('help.open')} aria-expanded={open} title={t('help.open')} onClick={() => setOpen(!open)}>
      <b style={{ fontSize: 16 }} aria-hidden="true">
        ?
      </b>
    </button>
  );
}

/**
 * Botão "Ajuda" no cabeçalho de cada tela (PageHead): abre o painel no artigo desta tela — o mesmo do "?" da barra,
 * mas visível onde a pessoa está olhando. Sem HelpProvider (telas públicas) não aparece.
 */
export function PageHelpButton() {
  const { t } = useI18n<CommonKey>();
  const help = useContext(HelpCtx);
  if (!help) return null;
  const { open, setOpen } = help;
  return (
    <button
      type="button"
      className="btn btn-sm btn-ghost page-help-btn"
      aria-expanded={open}
      aria-haspopup="dialog"
      title={t('help.open')}
      onClick={() => setOpen(!open)}
    >
      <Icon name="info" size="sm" />
      {t('help.pageButton')}
    </button>
  );
}

type Msg = { me: boolean; text: string; answer?: HelpAnswerData };

/** Busca do painel: o "Carregando…" sempre termina (resultado, erro ou tempo esgotado). */
type SearchState = { status: 'idle' } | { status: 'loading' } | { status: 'done'; hits: HelpHitData[] } | { status: 'error' };

/** Sem resposta do núcleo nesse tempo, a busca vira erro em vez de ficar carregando. */
const SEARCH_TIMEOUT_MS = 15000;

function HelpPanel({ source, onOpenWizard }: { source: HelpSource; onOpenWizard?: (id: string) => void }) {
  const { t, lang } = useI18n<CommonKey>();
  const { screen, wizard, setOpen, whatIs, setWhatIs, openManual } = useHelp();
  const toast = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [tab, setTab] = useState<'ctx' | 'ask'>('ctx');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState<SearchState>({ status: 'idle' });
  const [ctx, setCtx] = useState<{ article?: HelpArticleData; stepText?: string } | null>(null);
  const [article, setArticle] = useState<HelpArticleData | null>(null);
  // seção do trecho encontrado: o painel rola até ela quando o artigo abre
  const [anchor, setAnchor] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);
  const openReq = useRef(0);
  const [failed, setFailed] = useState(false);
  const [chat, setChat] = useState<Msg[]>([]);
  const [asking, setAsking] = useState(false);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Esc dentro do painel fecha a ajuda e devolve o foco ao botão "?".
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => {
    closeBtn.current?.focus();
    const prev = document.getElementById('help-btn');
    return () => prev?.focus();
  }, []);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    source
      .context({ screen, wizard: wizard?.id, step: wizard?.stepId, lang })
      .then((c) => alive && setCtx(c))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [source, screen, wizard?.id, wizard?.stepId, lang]);

  // cada letra digitada cancela a busca anterior: resposta atrasada não sobrescreve a nova nem deixa "Carregando…" preso
  useEffect(() => {
    const text = q.trim();
    if (!text) {
      setSearch({ status: 'idle' });
      return;
    }
    setSearch({ status: 'loading' });
    const ctl = new AbortController();
    const limit = window.setTimeout(() => {
      ctl.abort();
      setSearch({ status: 'error' });
    }, SEARCH_TIMEOUT_MS);
    const settle = (s: SearchState) => {
      if (ctl.signal.aborted) return;
      window.clearTimeout(limit);
      setSearch(s);
    };
    const debounce = window.setTimeout(() => {
      source
        .search({ q: text, screen, lang, signal: ctl.signal })
        .then((r) => settle({ status: 'done', hits: r ?? [] }))
        .catch(() => settle({ status: 'error' }));
    }, 250);
    return () => {
      ctl.abort();
      window.clearTimeout(debounce);
      window.clearTimeout(limit);
    };
  }, [q, screen, lang, source]);

  // abre o artigo no próprio painel (o resultado de outra tela abre o artigo daquela tela); só vale o último clique
  const openArticle = useCallback(
    (id?: string, section?: string) => {
      if (!id) return;
      const n = ++openReq.current;
      setOpening(true);
      setOpenFailed(false);
      source
        .article({ id, lang })
        .then((a) => {
          if (n !== openReq.current) return;
          setArticle(a);
          setAnchor(section || null);
          setTab('ctx');
        })
        .catch(() => n === openReq.current && setOpenFailed(true))
        .finally(() => n === openReq.current && setOpening(false));
    },
    [source, lang],
  );

  const searching = q.trim() !== '';
  const hits = search.status === 'done' ? search.hits : [];
  const results = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  // setas andam pelos resultados; Enter (ou espaço) no resultado abre o artigo
  const moveFocus = (from: number, delta: number) => {
    const items = results.current?.querySelectorAll<HTMLButtonElement>('button.list-item');
    if (!items?.length) return;
    if (from + delta < 0) {
      searchInput.current?.focus();
      return;
    }
    items[Math.min(items.length - 1, from + delta)]?.focus();
  };

  const ask = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const input = form.elements.namedItem('question') as HTMLInputElement;
    const text = input.value.trim();
    if (!text) return;
    setChat((c) => [...c, { me: true, text }]);
    input.value = '';
    setAsking(true);
    try {
      const a = await source.ask({ question: text, screen, lang, wizard: wizard?.id, step: wizard?.stepId });
      setChat((c) => [...c, { me: false, text, answer: a }]);
    } catch {
      setChat((c) => [...c, { me: false, text, answer: { answered: false, sections: [] } }]);
    } finally {
      setAsking(false);
      input.focus();
    }
  };

  const offerTicket = async () => {
    const ok = await confirm({ title: t('help.ticketTitle'), text: t('help.ticketText'), ok: t('help.ticketOk') });
    if (ok) toast(t('help.ticketDone'), { kind: 'info', timeout: 9000 });
  };

  const shown = article ?? ctx?.article;
  // com um artigo aberto, ele fica na frente dos resultados (antes os resultados cobriam o artigo e o clique "não fazia nada")
  const showResults = searching && !article;

  // links entre artigos (/help/<id>#seção) abrem dentro do painel, sem sair da tela (Enter num link também dispara "click")
  const body = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const el = body.current;
    if (!el) return;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a');
      const m = /^\/help\/([a-z0-9-]+)(?:#([a-z0-9-]+))?/.exec(a?.getAttribute('href') ?? '');
      if (!m || e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      openArticle(m[1], m[2]);
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [openArticle, shown, showResults]);

  // artigo aberto: foco no título (teclado e leitor de tela) e rolagem até a seção do trecho, ou para o topo
  useEffect(() => {
    if (!article) return;
    titleRef.current?.focus({ preventScroll: true });
    const target = anchor ? Array.from(body.current?.querySelectorAll<HTMLElement>('[id]') ?? []).find((e) => e.id === anchor) : null;
    if (target) target.scrollIntoView?.({ block: 'start' });
    else if (scroller.current) scroller.current.scrollTop = 0;
  }, [article, anchor]);

  return createPortal(
    <div ref={panel} className="help-panel" role="complementary" aria-label={t('help.title')}>
      <div className="row between" style={{ padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
        <b>{t('help.title')}</b>
        <button ref={closeBtn} type="button" className="icon-btn" aria-label={t('help.close')} onClick={() => setOpen(false)}>
          <Icon name="x" />
        </button>
      </div>
      <div className="tabs" role="tablist" aria-label={t('help.title')}>
        {(['ctx', 'ask'] as const).map((id) => (
          <button key={id} type="button" className="tab" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}>
            {id === 'ctx' ? t('help.tabContext') : t('help.tabAsk')}
          </button>
        ))}
      </div>
      <div ref={scroller} className="stack-sm" style={{ padding: '12px 14px', overflowY: 'auto', flex: 1 }}>
        {tab === 'ctx' ? (
          <>
            <div className="field">
              <label htmlFor="help-q">{t('help.search')}</label>
              <input
                ref={searchInput}
                id="help-q"
                type="search"
                value={q}
                placeholder={t('help.searchPlaceholder')}
                onChange={(e) => {
                  // digitar de novo volta aos resultados
                  setQ(e.target.value);
                  setArticle(null);
                  setAnchor(null);
                  setOpenFailed(false);
                }}
                onKeyDown={(e) => {
                  if (!showResults || !hits.length) return;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    moveFocus(-1, 1);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    openArticle(hits[0]?.id, hits[0]?.anchor);
                  }
                }}
              />
            </div>
            {opening && <Spinner label={t('ui.loading')} />}
            {openFailed && (
              <p className="small muted" role="alert">
                {t('help.articleFailed')}
              </p>
            )}
            {showResults ? (
              search.status === 'loading' ? (
                <Spinner label={t('ui.loading')} />
              ) : search.status === 'error' ? (
                <p className="small muted" role="alert">
                  {t('help.searchFailed')}
                </p>
              ) : hits.length ? (
                <div ref={results} className="list">
                  {hits.map((h, i) => (
                    <button
                      key={(h.id ?? '') + i}
                      type="button"
                      className="list-item"
                      onClick={() => openArticle(h.id, h.anchor)}
                      onKeyDown={(e) => {
                        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
                        e.preventDefault();
                        moveFocus(i, e.key === 'ArrowDown' ? 1 : -1);
                      }}
                    >
                      <span className="t">{h.title}</span>
                      <span />
                      <span className="m">{h.snippet}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="small muted">{t('help.noResults')}</p>
              )
            ) : (
              <>
                {failed && !article && <p className="small muted">{t('help.unavailable')}</p>}
                {!ctx && !failed && !article && <Spinner label={t('ui.loading')} />}
                {wizard && ctx?.stepText && !article && (
                  <div className="well small stack-sm">
                    <span className="kind">{t('help.step', { n: wizard.n, total: wizard.total })}</span>
                    <b>{wizard.title}</b>
                    <Markdown source={ctx.stepText} />
                  </div>
                )}
                {shown && (
                  <article className="stack-sm">
                    {article && (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        style={{ justifySelf: 'start' }}
                        onClick={() => {
                          setArticle(null);
                          setAnchor(null);
                        }}
                      >
                        <Icon name="arrowL" size="sm" />
                        {searching ? t('help.backToResults') : t('help.backToScreen')}
                      </button>
                    )}
                    {shown.sectionName && <span className="kind">{shown.sectionName}</span>}
                    <h2 ref={titleRef} tabIndex={-1} style={{ fontSize: 16 }}>
                      {shown.title}
                    </h2>
                    <div className="small" ref={body}>
                      <Markdown source={shown.markdown ?? ''} headingOffset={2} />
                    </div>
                    {openManual && shown.id && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ justifySelf: 'start' }}
                        onClick={() => {
                          if (article && anchor) openManual(shown.id!, anchor);
                          else openManual(shown.id!);
                          setOpen(false);
                        }}
                      >
                        <Icon name="doc" size="sm" />
                        {t('help.openManual')}
                      </button>
                    )}
                  </article>
                )}
              </>
            )}
            <div style={{ marginTop: 8 }}>
              <Switch
                checked={whatIs}
                onChange={(v) => {
                  setWhatIs(v);
                  toast(v ? t('help.whatIsOn') : t('help.whatIsOff'), { kind: 'info' });
                }}
                label={
                  <span className="small">
                    <b>{t('help.whatIs')}</b> {t('help.whatIsDesc')}
                  </span>
                }
              />
            </div>
          </>
        ) : (
          <>
            <div className="stack-sm" aria-live="polite">
              {chat.length === 0 && <p className="small muted">{t('help.askEmpty')}</p>}
              {chat.map((m, i) =>
                m.me ? (
                  <div key={i} className="well small">
                    <b>{t('help.you')}:</b> {m.text}
                  </div>
                ) : (
                  <div key={i} className="well small stack-sm">
                    {m.answer?.aiUsed && m.answer.answer ? (
                      <span style={{ whiteSpace: 'pre-wrap' }}>
                        <b>{t('help.assistant')}:</b> {m.answer.answer}
                        <span className="kind" style={{ display: 'block', marginTop: 4 }}>
                          {t('help.aiAnswer')}
                        </span>
                      </span>
                    ) : (
                      <span>
                        <b>{t('help.assistant')}:</b> {m.answer?.answered ? t('help.found') : t('help.noAnswer')}
                        {m.answer?.reason && <span className="xs muted" style={{ display: 'block' }}>{t('help.noAi', { reason: m.answer.reason })}</span>}
                      </span>
                    )}
                    {(m.answer?.sections ?? []).map((s, j) => (
                      <button key={j} type="button" className="link-btn" style={{ textAlign: 'left' }} onClick={() => openArticle(s.id, s.anchor)}>
                        {s.title}
                      </button>
                    ))}
                    {m.answer?.suggestedWizard && onOpenWizard && (
                      <button type="button" className="btn btn-sm btn-primary" style={{ justifySelf: 'start' }} onClick={() => {
                          onOpenWizard(m.answer!.suggestedWizard!);
                          setOpen(false);
                        }}>
                        {t('help.openWizard')}
                      </button>
                    )}
                    {m.answer?.support && (
                      <button type="button" className="btn btn-sm" style={{ justifySelf: 'start' }} onClick={offerTicket}>
                        {t('help.offerTicket')}
                      </button>
                    )}
                  </div>
                ),
              )}
              {asking && <Spinner label={t('ui.loading')} />}
            </div>
            <form className="row" style={{ marginTop: 8 }} onSubmit={ask}>
              <label htmlFor="help-ask" className="sr-only">
                {t('help.askPlaceholder')}
              </label>
              <input id="help-ask" name="question" type="text" maxLength={500} style={{ flex: 1, minWidth: 0 }} placeholder={t('help.askPlaceholder')} />
              <button type="submit" className="btn btn-primary btn-sm" disabled={asking}>
                {t('help.askButton')}
              </button>
            </form>
            <span className="xs muted">{t('help.askHint')}</span>
          </>
        )}
      </div>
      {confirmNode}
    </div>,
    document.body,
  );
}

const TIP_TARGETS = 'main input, main select, main textarea, main .opt, .modal input, .modal select, .modal textarea, .modal .opt';

/** "O que é isto?": ao passar o mouse ou focar num campo, mostra o rótulo e a explicação dele. */
function WhatIsThis({ enabled }: { enabled: boolean }) {
  const { t } = useI18n<CommonKey>();
  const [tip, setTip] = useState<{ title: string; text: string; top: number; left: number } | null>(null);
  useEffect(() => {
    if (!enabled) {
      setTip(null);
      return;
    }
    const show = (el: Element) => {
      const field = el.closest('.field, .opt, .switch, fieldset, [data-help]');
      let title = '';
      if (el.id) title = document.querySelector(`label[for="${CSS.escape(el.id)}"]`)?.textContent?.trim() ?? '';
      if (!title) title = (field?.querySelector('label, legend, .opt-title')?.textContent ?? el.getAttribute('aria-label') ?? '').trim();
      if (!title) return;
      const help =
        el.closest('[data-help]')?.getAttribute('data-help') ??
        field?.querySelector('.hint, .opt-desc')?.textContent?.trim() ??
        '';
      const r = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      setTip({ title: title.slice(0, 80), text: help || t('help.fieldTipDefault'), top: r.bottom + window.scrollY + 6, left: Math.max(8, Math.min(r.left + window.scrollX, vw - 308)) });
    };
    const onIn = (e: Event) => {
      const el = (e.target as Element | null)?.closest?.(TIP_TARGETS);
      if (el) show(el);
    };
    const onOut = () => setTip(null);
    document.addEventListener('focusin', onIn);
    document.addEventListener('mouseover', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('mouseover', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, [enabled, t]);
  if (!tip) return null;
  return createPortal(
    <div className="tip-bubble" role="tooltip" style={{ top: tip.top, left: tip.left }}>
      <b>{tip.title}</b>
      <br />
      {tip.text}
    </div>,
    document.body,
  );
}
